"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, FileCheck2, Microscope, SlidersHorizontal, Wrench, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface ServiceItem {
  id: string;
  category: string;
  title: string;
  description: string;
  feature1: string;
  feature2: string;
  feature3: string;
  iconName: string;
  linkPath: string;
  actionText: string;
}
const serviceItems: ServiceItem[] = [{
  id: "monitoring",
  category: "Process Analytics",
  title: "In-Line Monitoring Solutions",
  description: "Continuous process analytical technology (PAT) integration for real-time blend uniformity, moisture analysis, and reaction tracking.",
  feature1: "Real-time drying endpoint detection",
  feature2: "Non-destructive spectral analysis",
  feature3: "Automated alert triggers & control loops",
  iconName: "activity",
  linkPath: "/ProcessAnalyticalTechnologyPage",
  actionText: "Explore PAT solutions"
}, {
  id: "compliance",
  category: "Regulatory",
  title: "Qualification & Compliance Support",
  description: "Validation lifecycle services and documentation support for regulated manufacturing and laboratory environments.",
  feature1: "IQ/OQ/PQ protocol design & execution",
  feature2: "Data integrity assessments",
  feature3: "Periodic audit review documentation",
  iconName: "fileCheck",
  linkPath: "/ServicesPage",
  actionText: "View qualification protocols"
}, {
  id: "consulting",
  category: "Methodology",
  title: "Analytical Method Consulting",
  description: "Custom chemometric modeling, spectral library construction, and feasibility studies tailored to complex pharmaceutical formulations.",
  feature1: "Multivariate data analysis & models",
  feature2: "Spectral library curation & validation",
  feature3: "Method transfer across lab & plant",
  iconName: "microscope",
  linkPath: "/SolutionsPage",
  actionText: "Consult our specialists"
}, {
  id: "maintenance",
  category: "Lifecycle Care",
  title: "Preventive Maintenance & Calibration",
  description: "Planned preventative maintenance programs and traceable optical recalibration to maximize instrument uptime and measurement accuracy.",
  feature1: "Certified calibration standards",
  feature2: "Rapid on-site emergency response",
  feature3: "Annual service plan options",
  iconName: "wrench",
  linkPath: "/ContactPage",
  actionText: "Schedule equipment service"
}, {
  id: "testing",
  category: "Physical Characterization",
  title: "Powder & Tablet Testing Systems",
  description: "Specialized instrumentation and support for bulk density, flowability, shear testing, and finished solid dose physical attributes.",
  feature1: "Automated tablet hardness & disintegration",
  feature2: "Powder rheology and compressibility",
  feature3: "Standardized pharmacopeia test routines",
  iconName: "sliders",
  linkPath: "/PowderTestingPage",
  actionText: "Learn about powder testing"
}, {
  id: "materials",
  category: "Security & Safety",
  title: "Raw Material ID & Safety Inspection",
  description: "Handheld and benchtop spectroscopic verification tools for incoming goods inspection, counterfeit detection, and hazardous material safety.",
  feature1: "Direct container inspection",
  feature2: "Through-barrier container scanning",
  feature3: "Instant pass/fail compliance logs",
  iconName: "shieldCheck",
  linkPath: "/RawMaterialIdentificationPage",
  actionText: "Review material ID tools"
}];
function getServiceIcon(iconName: string) {
  switch (iconName) {
    case "activity":
      return <Activity className="size-6 text-primary" />;
    case "fileCheck":
      return <FileCheck2 className="size-6 text-primary" />;
    case "microscope":
      return <Microscope className="size-6 text-primary" />;
    case "wrench":
      return <Wrench className="size-6 text-primary" />;
    case "sliders":
      return <SlidersHorizontal className="size-6 text-primary" />;
    case "shieldCheck":
      return <ShieldCheck className="size-6 text-primary" />;
    default:
      return <Activity className="size-6 text-primary" />;
  }
}
export default function Services() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0.2 : 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="serviceslist" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border/60">
      <div className="page-container">
        {/* Header Section */}
        <div className="max-w-3xl mb-14 lg:mb-18">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground font-mono">
              Laboratory & Process Lifecycle Services
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Precision engineering and compliance solutions for regulated science.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From initial analytical technology qualification to routine maintenance and real-time process monitoring, we support laboratories and manufacturing units across Ireland and the UK.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {serviceItems.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex-1 flex flex-col justify-between border-border bg-card hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div className="flex items-center justify-center size-12 rounded-lg bg-secondary border border-border/80">
                        {getServiceIcon(item.iconName)}
                      </div>
                      <Badge variant="secondary" className="font-medium text-xs rounded-full px-3 py-1">
                        {item.category}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold tracking-tight text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-6">
                    <div className="border-t border-border/60 pt-4 space-y-2.5">
                      <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.feature1}</span>
                      </div>
                      <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.feature2}</span>
                      </div>
                      <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.feature3}</span>
                      </div>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-0 border-t border-border/40 mt-auto">
                  <Button asChild variant="ghost" className="w-full justify-between px-0 hover:bg-transparent text-primary hover:text-primary/80 font-medium group text-sm">
                    <Link to={item.linkPath} className="flex items-center justify-between w-full">
                      <span>{item.actionText}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Contact Callout */}
        <div className="mt-14 lg:mt-18 p-6 lg:p-8 rounded-xl bg-secondary border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="text-lg font-semibold text-secondary-foreground">
              Require custom validation documentation or a site survey?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Our engineering specialists support custom method development, site audits, and SLA-backed preventative maintenance.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto">
              <Link to="/ContactPage">
                <span>Request service consultation</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}